import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, GraduationCap, Package, Megaphone, Landmark, Trash2, ExternalLink, Printer, Pencil, Wrench } from 'lucide-react';
import { useAuth } from '../AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const TABS = [
  { key: 'skill_training', label: 'Skill & Job Training', icon: GraduationCap, color: 'emerald' },
  { key: 'product', label: 'Products', icon: Package, color: 'blue' },
  { key: 'marketing', label: 'Marketing & Promotion', icon: Megaphone, color: 'purple' },
  { key: 'financial_support', label: 'Financial Support', icon: Landmark, color: 'amber' },
];

// Print helper — renders the active livelihoods tab as a printable HTML table
const printLivelihoodsList = (entries, branches, branchFilter, activeTab, TABS_LIST) => {
  const branchName = branchFilter === 'all'
    ? 'All Branches'
    : (branches.find(b => b.id === branchFilter)?.name || '-');
  const tab = TABS_LIST.find(t => t.key === activeTab);
  const fmtR = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN')}`;
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '-';
  let headers = [];
  let rowFn = () => '';
  if (activeTab === 'skill_training') {
    headers = ['#', 'Beneficiary', 'Branch', 'Training', 'Institute', 'Duration', 'Date', 'Status', 'Earn Before', 'Earn After'];
    rowFn = (e, i) => {
      const br = branches.find(b => b.id === e.branch_id);
      return `<tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
        <td>${i + 1}</td><td>${e.entrepreneur_name || '-'}</td><td>${br?.name || '-'}</td>
        <td>${e.training_name || '-'}</td><td>${e.institute || '-'}</td>
        <td>${e.training_duration || '-'}</td><td>${fmtD(e.training_date)}</td>
        <td>${e.training_status || '-'}</td>
        <td>${fmtR(e.earning_before_training)}</td><td>${fmtR(e.earning_after_training)}</td>
      </tr>`;
    };
  } else if (activeTab === 'product') {
    headers = ['#', 'Beneficiary', 'Branch', 'Product', 'Category', 'Qty / Month', 'Price', 'UDYAM No.'];
    rowFn = (e, i) => {
      const br = branches.find(b => b.id === e.branch_id);
      return `<tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
        <td>${i + 1}</td><td>${e.entrepreneur_name || '-'}</td><td>${br?.name || '-'}</td>
        <td>${e.product_name || '-'}</td><td>${e.product_category || '-'}</td>
        <td>${e.qty_per_month || '-'}</td><td>${fmtR(e.price_per_unit)}</td>
        <td>${e.udyam_registration_no || '-'}</td>
      </tr>`;
    };
  } else if (activeTab === 'marketing') {
    headers = ['#', 'Beneficiary', 'Branch', 'Activity', 'Type', 'Date', 'Venue', 'Outcome'];
    rowFn = (e, i) => {
      const br = branches.find(b => b.id === e.branch_id);
      return `<tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
        <td>${i + 1}</td><td>${e.entrepreneur_name || '-'}</td><td>${br?.name || '-'}</td>
        <td>${e.activity_name || '-'}</td><td>${e.activity_type || '-'}</td>
        <td>${fmtD(e.activity_date)}</td><td>${e.venue || '-'}</td><td>${e.outcome || '-'}</td>
      </tr>`;
    };
  } else if (activeTab === 'financial_support') {
    headers = ['#', 'Beneficiary', 'Branch', 'Source', 'Scheme', 'Applied', 'Sanctioned', 'Disbursed', 'Status'];
    rowFn = (e, i) => {
      const br = branches.find(b => b.id === e.branch_id);
      return `<tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
        <td>${i + 1}</td><td>${e.entrepreneur_name || '-'}</td><td>${br?.name || '-'}</td>
        <td>${e.source || '-'}</td><td>${e.scheme_name || '-'}</td>
        <td>${fmtR(e.applied_amount)}</td><td>${fmtR(e.sanctioned_amount)}</td>
        <td>${fmtR(e.disbursed_amount)}</td><td>${e.status || '-'}</td>
      </tr>`;
    };
  }
  const colCount = headers.length;
  const rows = entries.map(rowFn).join('');
  const html = `<!doctype html><html><head><title>Livelihoods Cell — ${tab?.label || ''} — WWAGS MIS</title>
  <style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{color:#047857;font-size:20px;margin:0 0 4px}
    .meta{color:#64748b;font-size:12px;margin-bottom:14px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#047857;color:#fff;padding:8px;text-align:left}
    td{padding:6px 8px;border-bottom:1px solid #e2e8f0}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body>
    <h1>WWAGS MIS — Livelihoods Cell — ${tab?.label || ''}</h1>
    <p class="meta">Branch: <b>${branchName}</b> &nbsp;•&nbsp; Total Entries: <b>${entries.length}</b> &nbsp;•&nbsp; Generated: ${new Date().toLocaleString('en-IN')}</p>
    <table>
      <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${rows || `<tr><td colspan="${colCount}" style="text-align:center;color:#94a3b8;padding:20px">No entries</td></tr>`}</tbody>
    </table>
  </body></html>`;
  const w = window.open('', '_blank');
  if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
};

const Livelihoods = () => {
  const { user, isAdmin, isAdminOnly } = useAuth();
  // Branch scoping — Admin/Checker/Account/HR see all; others see home + allowed
  const canViewAll = isAdmin() || ['Checker', 'Account', 'HR'].includes(user?.role);
  const extraAllowed = Array.isArray(user?.allowed_branch_ids) ? user.allowed_branch_ids : [];
  const [activeTab, setActiveTab] = useState('skill_training');
  const [entries, setEntries] = useState([]);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Branch filter defaults: Admin/Checker/... → 'all'. Others → their home branch.
  const [branchFilter, setBranchFilter] = useState(canViewAll ? 'all' : (user?.branch_id || 'all'));
  const [formData, setFormData] = useState({});
  const [selectedEntDetails, setSelectedEntDetails] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  // Branches visible to this user in dropdowns
  const visibleBranches = canViewAll
    ? branches
    : branches.filter((b) => b.id === user?.branch_id || extraAllowed.includes(b.id));

  useEffect(() => {
    api.get('/entrepreneurs', { params: { limit: 5000 } }).then(r => setEntrepreneurs(r.data)).catch(() => {});
    api.get('/branches').then(r => setBranches(r.data)).catch(() => {});
    api.get('/livelihoods/summary').then(r => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchEntries(); }, [activeTab, branchFilter]);

  // Real-time auto-refresh (silent — no spinner / toast)
  useAutoRefresh(() => { fetchEntries(true); refreshSummary(); });

  const fetchEntrepreneurDetails = async (entId) => {
    if (!entId) { setSelectedEntDetails(null); return; }
    try {
      const ent = entrepreneurs.find(e => e.id === entId);
      let loans = [];
      try {
        const loanResp = await api.get('/loans', { params: { entrepreneur_id: entId } });
        loans = loanResp.data || [];
      } catch {}
      setSelectedEntDetails({ ...ent, loans });
      // Auto-fill branch
      if (ent?.branch_id) {
        setFormData(prev => ({ ...prev, branch_id: ent.branch_id }));
      }
    } catch { setSelectedEntDetails(null); }
  };

  const fetchEntries = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { category: activeTab };
      if (branchFilter !== 'all') params.branch_id = branchFilter;
      const r = await api.get('/livelihoods', { params });
      setEntries(r.data);
    } catch { if (!silent) toast.error('Failed to fetch entries'); }
    finally { if (!silent) setLoading(false); }
  };

  const refreshSummary = async () => {
    try { const r = await api.get('/livelihoods/summary'); setSummary(r.data); } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await api.put(`/livelihoods/${editingEntry.id}`, { ...formData, category: activeTab });
        toast.success('Entry updated successfully');
      } else {
        await api.post('/livelihoods', { ...formData, category: activeTab });
        toast.success('Entry added successfully');
      }
      setDialogOpen(false);
      setEditingEntry(null);
      setFormData({});
      setSelectedEntDetails(null);
      fetchEntries();
      refreshSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleEdit = (entry) => {
    // Populate form with the entry's current fields; category comes from activeTab
    setEditingEntry(entry);
    setFormData({
      entrepreneur_id: entry.entrepreneur_id || '',
      branch_id: entry.branch_id || '',
      training_name: entry.training_name || '',
      institute: entry.institute || '',
      training_duration: entry.training_duration || '',
      training_date: entry.training_date || '',
      training_status: entry.training_status || '',
      earning_before_training: entry.earning_before_training || '',
      earning_after_training: entry.earning_after_training || '',
      employer_name: entry.employer_name || '',
      date_of_joining: entry.date_of_joining || '',
      monthly_revenue: entry.monthly_revenue || '',
      product_name: entry.product_name || '',
      product_type: entry.product_type || '',
      quantity: entry.quantity || '',
      pricing: entry.pricing || '',
      activity_name: entry.activity_name || '',
      activity_type: entry.activity_type || '',
      event_date: entry.event_date || '',
      description: entry.description || '',
      institution_name: entry.institution_name || '',
      institution_type: entry.institution_type || '',
      support_amount: entry.support_amount || '',
      support_date: entry.support_date || '',
      support_status: entry.support_status || '',
      remarks: entry.remarks || '',
      udyam_registration_no: entry.udyam_registration_no || '',
      business_address: entry.business_address || '',
    });
    if (entry.entrepreneur_id) fetchEntrepreneurDetails(entry.entrepreneur_id);
    setDialogOpen(true);
  };

  const runBranchBackfill = async () => {
    if (!window.confirm('Re-align every livelihood entry to its entrepreneur\'s actual branch? Recommended after past data-entry errors.')) return;
    try {
      const r = await api.post('/livelihoods/backfill-branches');
      toast.success(`Corrected ${r.data.corrected} of ${r.data.scanned} entries`);
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Backfill failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`/livelihoods/${id}`); toast.success('Deleted'); fetchEntries(); refreshSummary(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const renderForm = () => {
    // Filter entrepreneurs by the selected branch (so users pick Branch first)
    const filteredEnts = formData.branch_id
      ? entrepreneurs.filter(e => e.branch_id === formData.branch_id)
      : entrepreneurs;

    const brSelect = (
      <div className="space-y-2">
        <Label>Branch *</Label>
        <Select value={formData.branch_id || ''} onValueChange={v => {
          // Changing branch clears any previously-picked entrepreneur that no longer belongs
          const stillValid = entrepreneurs.find(e => e.id === formData.entrepreneur_id)?.branch_id === v;
          setFormData({ ...formData, branch_id: v, entrepreneur_id: stillValid ? formData.entrepreneur_id : '' });
          if (!stillValid) setSelectedEntDetails(null);
        }}>
          <SelectTrigger data-testid="livelihood-branch-select"><SelectValue placeholder="Select branch first" /></SelectTrigger>
          <SelectContent>{visibleBranches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.id === user?.branch_id ? ' (Home)' : ''}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
    const entSelect = (
      <div className="space-y-2">
        <Label>Link to Entrepreneur</Label>
        <Select
          value={formData.entrepreneur_id || ''}
          onValueChange={v => { setFormData({ ...formData, entrepreneur_id: v }); fetchEntrepreneurDetails(v); }}
          disabled={!formData.branch_id}
        >
          <SelectTrigger data-testid="livelihood-entrepreneur-select">
            <SelectValue placeholder={formData.branch_id ? 'Select entrepreneur (optional)' : 'Pick a branch first'} />
          </SelectTrigger>
          <SelectContent>
            {filteredEnts.length === 0 && formData.branch_id ? (
              <div className="px-2 py-3 text-xs text-slate-500">No entrepreneurs found for this branch.</div>
            ) : filteredEnts.map(e => (
              <SelectItem key={e.id} value={e.id}>{e.name} ({e.mobile})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );

    const entDetailsCard = selectedEntDetails ? (
      <div className="bg-amber-50 rounded-lg border border-amber-200 p-3 space-y-1">
        <p className="text-xs font-semibold text-amber-800 mb-1.5">Entrepreneur Details (Auto-fetched)</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          <div><span className="text-amber-600">Enterprise:</span> <span className="font-medium text-slate-900">{selectedEntDetails.enterprise?.name || '-'}</span></div>
          <div><span className="text-amber-600">Sector:</span> <span className="font-medium text-slate-900">{selectedEntDetails.enterprise?.type || '-'}</span></div>
          <div><span className="text-amber-600">Trade:</span> <span className="font-medium text-slate-900">{selectedEntDetails.enterprise?.operation_type || '-'}</span></div>
          <div><span className="text-amber-600">Branch:</span> <span className="font-medium text-slate-900">{branches.find(b => b.id === selectedEntDetails.branch_id)?.name || '-'}</span></div>
          <div><span className="text-amber-600">SHG/VLF:</span> <span className="font-medium text-slate-900">{selectedEntDetails.shg_vlf_name || '-'}</span></div>
          <div><span className="text-amber-600">Mobile:</span> <span className="font-medium text-slate-900">{selectedEntDetails.mobile || '-'}</span></div>
        </div>
        {selectedEntDetails.loans && selectedEntDetails.loans.length > 0 ? (
          <div className="mt-2 pt-2 border-t border-amber-200">
            <p className="text-xs font-semibold text-amber-800 mb-1">Loan(s) Taken ({selectedEntDetails.loans.length})</p>
            <div className="space-y-1">
              {selectedEntDetails.loans.map(l => (
                <div key={l.loan_id} className="flex items-center gap-3 text-xs bg-white rounded px-2 py-1 border border-amber-100">
                  <span className="font-medium text-blue-700">{l.loan_id}</span>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px]">{l.loan_type}</span>
                  <span className="font-semibold">Rs.{(l.loan_amount || 0).toLocaleString('en-IN')}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${l.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{l.status}</span>
                  <span className="text-slate-400">O/S: Rs.{(l.outstanding_amount || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-amber-600 mt-1 pt-1 border-t border-amber-200">No loans found for this entrepreneur</p>
        )}
      </div>
    ) : null;

    if (activeTab === 'skill_training') return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">{brSelect}{entSelect}</div>
        {entDetailsCard}

        {/* Section: Training Details */}
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4 space-y-4">
          <p className="text-xs font-semibold text-emerald-800">Training / Skill Details</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Name of Training / Skill *</Label>
              <Input value={formData.training_name || ''} onChange={e => setFormData({ ...formData, training_name: e.target.value })} required placeholder="e.g. Food Processing, Tailoring, Computer" />
              <p className="text-[10px] text-slate-400">What training or skill did they learn?</p>
            </div>
            <div className="space-y-1">
              <Label>Training Institute / Organization</Label>
              <Input value={formData.institute || ''} onChange={e => setFormData({ ...formData, institute: e.target.value })} placeholder="e.g. ISDE Manipur, NIESBUD, ITI" />
              <p className="text-[10px] text-slate-400">Name of the institute that conducted the training</p>
            </div>
            <div className="space-y-1">
              <Label>Training Duration</Label>
              <Input value={formData.training_duration || ''} onChange={e => setFormData({ ...formData, training_duration: e.target.value })} placeholder="e.g. 15 days, 3 months" />
              <p className="text-[10px] text-slate-400">How long was the training?</p>
            </div>
            <div className="space-y-1">
              <Label>Training Date</Label>
              <Input type="date" value={formData.training_date || ''} onChange={e => setFormData({ ...formData, training_date: e.target.value })} />
              <p className="text-[10px] text-slate-400">When was the training conducted?</p>
            </div>
            <div className="space-y-1">
              <Label>Training Status</Label>
              <Select value={formData.training_status || ''} onValueChange={v => setFormData({ ...formData, training_status: v })}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent><SelectItem value="Planned">Planned</SelectItem><SelectItem value="Ongoing">Ongoing</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="Dropped">Dropped</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Section: After Training — Job or Business (simplified) */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 space-y-4">
          <p className="text-xs font-semibold text-blue-800">After Training — Job / Business</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Start Date (Job / Business)</Label>
              <Input type="date" value={formData.date_of_joining || ''} onChange={e => setFormData({ ...formData, date_of_joining: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Employer / Business Name</Label>
              <Input value={formData.employer_name || ''} onChange={e => setFormData({ ...formData, employer_name: e.target.value })} placeholder="Company / own business name" />
            </div>
            <div className="space-y-1">
              <Label>Earning Before (Rs./mo)</Label>
              <Input type="number" value={formData.earning_before_training || ''} onChange={e => setFormData({ ...formData, earning_before_training: e.target.value })} placeholder="0" />
            </div>
            <div className="space-y-1">
              <Label>Earning After (Rs./mo)</Label>
              <Input type="number" value={formData.earning_after_training || ''} onChange={e => setFormData({ ...formData, earning_after_training: e.target.value })} placeholder="0" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Business Location / Address</Label>
              <textarea
                value={formData.business_address || ''}
                onChange={e => setFormData({ ...formData, business_address: e.target.value })}
                placeholder="Shop no., street, village/town, district, PIN"
                rows={2}
                className="w-full text-sm rounded-md border border-slate-300 px-3 py-2"
                data-testid="business-address-input"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Avg. Monthly Revenue (if running business)</Label>
              <Input type="number" value={formData.monthly_revenue || ''} onChange={e => setFormData({ ...formData, monthly_revenue: e.target.value })} placeholder="e.g. 25000" />
              {formData.monthly_revenue > 0 && (
                <p className="text-xs text-emerald-700 font-semibold mt-1">Estimated Annual Revenue: Rs.{(parseFloat(formData.monthly_revenue) * 12).toLocaleString('en-IN')}</p>
              )}
            </div>
          </div>

          {/* Photo upload (up to 3) */}
          <div className="pt-3 border-t border-blue-200">
            <Label className="text-xs font-semibold text-blue-800">Photographs of Business / Training (up to 3)</Label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []).slice(0, 3 - (formData.photos?.length || 0));
                const encoded = await Promise.all(files.map(f => new Promise((resolve) => {
                  const r = new FileReader();
                  r.onload = () => resolve(r.result);
                  r.readAsDataURL(f);
                })));
                setFormData({ ...formData, photos: [...(formData.photos || []), ...encoded].slice(0, 3) });
                e.target.value = '';
              }}
              className="mt-1 block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-100 file:text-blue-800 hover:file:bg-blue-200"
              data-testid="livelihood-photos-input"
              disabled={(formData.photos?.length || 0) >= 3}
            />
            {formData.photos && formData.photos.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {formData.photos.map((src, idx) => (
                  <div key={idx} className="relative rounded-md overflow-hidden border border-slate-200 aspect-square bg-white">
                    <img src={src} alt={`Business ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, photos: formData.photos.filter((_, i) => i !== idx) })}
                      className="absolute top-1 right-1 bg-rose-600 text-white rounded-full w-5 h-5 text-xs leading-none flex items-center justify-center hover:bg-rose-700"
                      title="Remove"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-slate-500 mt-1">Max 3 images. JPG/PNG, ideally under 2 MB each.</p>
          </div>
        </div>

        <div className="space-y-2"><Label>Remarks</Label><Input value={formData.remarks || ''} onChange={e => setFormData({ ...formData, remarks: e.target.value })} placeholder="Any additional notes" /></div>
      </div>
    );

    if (activeTab === 'product') return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">{entSelect}{brSelect}</div>
        {entDetailsCard}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 space-y-4">
          <p className="text-xs font-semibold text-blue-800">Product Details</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1"><Label>Product Name *</Label><Input value={formData.product_name || ''} onChange={e => setFormData({ ...formData, product_name: e.target.value })} required placeholder="e.g. Handloom Shawl, Pickle, Bamboo Craft" /><p className="text-[10px] text-slate-400">Name of the product made by the beneficiary</p></div>
            <div className="space-y-1"><Label>Product Type</Label>
              <Select value={formData.product_type || ''} onValueChange={v => setFormData({ ...formData, product_type: v })}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent><SelectItem value="Handicraft">Handicraft</SelectItem><SelectItem value="Food Processing">Food Processing</SelectItem><SelectItem value="Textile">Textile</SelectItem><SelectItem value="Agriculture">Agriculture</SelectItem><SelectItem value="Livestock">Livestock</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            <p className="text-[10px] text-slate-400">Category of the product</p></div>
            <div className="space-y-1"><Label>Quantity / Volume</Label><Input value={formData.quantity || ''} onChange={e => setFormData({ ...formData, quantity: e.target.value })} placeholder="e.g. 100 units/month" /><p className="text-[10px] text-slate-400">How much is produced per month?</p></div>
            <div className="space-y-1"><Label>Pricing (Rs.)</Label><Input value={formData.pricing || ''} onChange={e => setFormData({ ...formData, pricing: e.target.value })} placeholder="e.g. Rs.500 per unit" /><p className="text-[10px] text-slate-400">Selling price per unit</p></div>
          </div>
        </div>
        <div className="space-y-1"><Label>UDYAM Registration (MSME) No.</Label><Input value={formData.udyam_registration_no || ''} onChange={e => setFormData({ ...formData, udyam_registration_no: e.target.value })} placeholder="e.g. UDYAM-MN-01-0012345" /><p className="text-[10px] text-slate-400">MSME/Udyam registration number if registered</p></div>
        <div className="space-y-2"><Label>Remarks</Label><Input value={formData.remarks || ''} onChange={e => setFormData({ ...formData, remarks: e.target.value })} /></div>
      </div>
    );

    if (activeTab === 'marketing') return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">{entSelect}{brSelect}</div>
        {entDetailsCard}
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-4 space-y-4">
          <p className="text-xs font-semibold text-purple-800">Marketing & Promotion Details</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1"><Label>Activity / Event Name *</Label><Input value={formData.activity_name || ''} onChange={e => setFormData({ ...formData, activity_name: e.target.value })} required placeholder="e.g. Sangai Festival, Local Mela" /><p className="text-[10px] text-slate-400">Name of the marketing event or activity</p></div>
            <div className="space-y-1"><Label>Activity Type</Label>
              <Select value={formData.activity_type || ''} onValueChange={v => setFormData({ ...formData, activity_type: v })}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent><SelectItem value="Exhibition">Exhibition</SelectItem><SelectItem value="Mela / Fair">Mela / Fair</SelectItem><SelectItem value="Online Marketing">Online Marketing</SelectItem><SelectItem value="Door-to-Door">Door-to-Door</SelectItem><SelectItem value="Retail Linkage">Retail Linkage</SelectItem><SelectItem value="Brand Building">Brand Building</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            <p className="text-[10px] text-slate-400">Type of marketing/promotion activity</p></div>
            <div className="space-y-1"><Label>Event Date</Label><Input type="date" value={formData.event_date || ''} onChange={e => setFormData({ ...formData, event_date: e.target.value })} /><p className="text-[10px] text-slate-400">When did the event take place?</p></div>
          </div>
        </div>
        <div className="space-y-1"><Label>Description</Label><Input value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description of the activity" /><p className="text-[10px] text-slate-400">Describe the marketing/promotion activity</p></div>
        <div className="space-y-2"><Label>Remarks</Label><Input value={formData.remarks || ''} onChange={e => setFormData({ ...formData, remarks: e.target.value })} /></div>
      </div>
    );

    if (activeTab === 'financial_support') return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">{entSelect}{brSelect}</div>
        {entDetailsCard}
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 space-y-4">
          <p className="text-xs font-semibold text-amber-800">Financial Support / Linkage Details</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1"><Label>Institution Name *</Label><Input value={formData.institution_name || ''} onChange={e => setFormData({ ...formData, institution_name: e.target.value })} required placeholder="e.g. SBI, Bandhan Bank, MANIREDA" /><p className="text-[10px] text-slate-400">Name of the bank, MFI, or institution providing support</p></div>
            <div className="space-y-1"><Label>Institution Type</Label>
              <Select value={formData.institution_type || ''} onValueChange={v => setFormData({ ...formData, institution_type: v })}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent><SelectItem value="MFI">MFI (Micro Finance)</SelectItem><SelectItem value="Bank">Bank</SelectItem><SelectItem value="SHG">SHG</SelectItem><SelectItem value="NBFC">NBFC</SelectItem><SelectItem value="Government Scheme">Government Scheme</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            <p className="text-[10px] text-slate-400">Type of financial institution</p></div>
            <div className="space-y-1"><Label>Loan / Support Amount (Rs.)</Label><Input type="number" value={formData.support_amount || ''} onChange={e => setFormData({ ...formData, support_amount: e.target.value })} placeholder="e.g. 50000" /><p className="text-[10px] text-slate-400">Amount of financial support received or applied</p></div>
            <div className="space-y-1"><Label>Date</Label><Input type="date" value={formData.support_date || ''} onChange={e => setFormData({ ...formData, support_date: e.target.value })} /><p className="text-[10px] text-slate-400">Date of application or disbursement</p></div>
            <div className="space-y-1"><Label>Status</Label>
              <Select value={formData.support_status || ''} onValueChange={v => setFormData({ ...formData, support_status: v })}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent><SelectItem value="Applied">Applied</SelectItem><SelectItem value="Sanctioned">Sanctioned</SelectItem><SelectItem value="Disbursed">Disbursed</SelectItem><SelectItem value="Rejected">Rejected</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
              </Select>
            <p className="text-[10px] text-slate-400">Current status of the financial support</p></div>
          </div>
        </div>
        <div className="space-y-2"><Label>Remarks</Label><Input value={formData.remarks || ''} onChange={e => setFormData({ ...formData, remarks: e.target.value })} /></div>
      </div>
    );
    return null;
  };

  const renderTable = () => {
    if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div></div>;
    if (entries.length === 0) return <div className="text-center py-12 text-slate-500"><p>No entries found for this category</p></div>;

    const tabConf = TABS.find(t => t.key === activeTab);
    const headColor = { emerald: 'bg-emerald-700', blue: 'bg-blue-700', purple: 'bg-purple-700', amber: 'bg-amber-600' }[tabConf.color];

    if (activeTab === 'skill_training') return (
      <table className="w-full text-sm">
        <thead className={`${headColor} text-white`}><tr>
          <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Training</th><th className="px-3 py-2 text-left">Institute</th><th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-right">Before (Rs.)</th><th className="px-3 py-2 text-right">After (Rs.)</th><th className="px-3 py-2 text-left">Employer</th><th className="px-3 py-2 text-right">Monthly Rev.</th><th className="px-3 py-2 text-right">Annual Rev.</th><th className="px-3 py-2 text-center">DOJ</th>{isAdminOnly() && <th className="px-3 py-2"></th>}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">{entries.map((e, i) => (
          <tr key={e.id} className={i % 2 ? 'bg-slate-50' : ''}>
            <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
            <td className="px-3 py-2 text-xs">{e.branch_name || '-'}</td>
            <td className="px-3 py-2 font-medium">{e.training_name}</td>
            <td className="px-3 py-2 text-xs">{e.institute || '-'}</td>
            <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${e.training_status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : e.training_status === 'Ongoing' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>{e.training_status || '-'}</span></td>
            <td className="px-3 py-2 text-right text-slate-600">{e.earning_before_training ? `Rs.${e.earning_before_training.toLocaleString('en-IN')}` : '-'}</td>
            <td className="px-3 py-2 text-right font-medium text-emerald-700">{e.earning_after_training ? `Rs.${e.earning_after_training.toLocaleString('en-IN')}` : '-'}</td>
            <td className="px-3 py-2 text-xs">{e.employer_name || '-'}</td>
            <td className="px-3 py-2 text-right">{e.monthly_revenue ? `Rs.${e.monthly_revenue.toLocaleString('en-IN')}` : '-'}</td>
            <td className="px-3 py-2 text-right font-bold text-blue-700">{e.monthly_revenue ? `Rs.${(e.monthly_revenue * 12).toLocaleString('en-IN')}` : '-'}</td>
            <td className="px-3 py-2 text-center text-xs">{e.date_of_joining ? new Date(e.date_of_joining).toLocaleDateString('en-IN') : '-'}</td>
            {isAdminOnly() && <td className="px-3 py-2"><div className="flex gap-0.5"><Button variant="ghost" size="sm" onClick={() => handleEdit(e)} className="text-blue-600 h-7 w-7 p-0" title="Edit" data-testid={`edit-lh-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="sm" onClick={() => handleDelete(e.id)} className="text-red-600 h-7 w-7 p-0" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button></div></td>}
          </tr>
        ))}</tbody>
      </table>
    );

    if (activeTab === 'product') return (
      <table className="w-full text-sm">
        <thead className={`${headColor} text-white`}><tr>
          <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Product Name</th><th className="px-3 py-2 text-center">Type</th><th className="px-3 py-2 text-center">Quantity</th><th className="px-3 py-2 text-right">Pricing</th><th className="px-3 py-2 text-left">Remarks</th>{isAdminOnly() && <th className="px-3 py-2"></th>}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">{entries.map((e, i) => (
          <tr key={e.id} className={i % 2 ? 'bg-slate-50' : ''}>
            <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
            <td className="px-3 py-2 text-xs">{e.branch_name || '-'}</td>
            <td className="px-3 py-2 font-medium">{e.product_name}</td>
            <td className="px-3 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{e.product_type || '-'}</span></td>
            <td className="px-3 py-2 text-center">{e.quantity || '-'}</td>
            <td className="px-3 py-2 text-right">{e.pricing || '-'}</td>
            <td className="px-3 py-2 text-xs text-slate-500">{e.remarks || '-'}</td>
            {isAdminOnly() && <td className="px-3 py-2"><div className="flex gap-0.5"><Button variant="ghost" size="sm" onClick={() => handleEdit(e)} className="text-blue-600 h-7 w-7 p-0" title="Edit" data-testid={`edit-lh-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="sm" onClick={() => handleDelete(e.id)} className="text-red-600 h-7 w-7 p-0" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button></div></td>}
          </tr>
        ))}</tbody>
      </table>
    );

    if (activeTab === 'marketing') return (
      <table className="w-full text-sm">
        <thead className={`${headColor} text-white`}><tr>
          <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Activity</th><th className="px-3 py-2 text-center">Type</th><th className="px-3 py-2 text-center">Date</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-left">Remarks</th>{isAdminOnly() && <th className="px-3 py-2"></th>}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">{entries.map((e, i) => (
          <tr key={e.id} className={i % 2 ? 'bg-slate-50' : ''}>
            <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
            <td className="px-3 py-2 text-xs">{e.branch_name || '-'}</td>
            <td className="px-3 py-2 font-medium">{e.activity_name}</td>
            <td className="px-3 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{e.activity_type || '-'}</span></td>
            <td className="px-3 py-2 text-center">{e.event_date ? new Date(e.event_date).toLocaleDateString('en-IN') : '-'}</td>
            <td className="px-3 py-2 text-xs">{e.description || '-'}</td>
            <td className="px-3 py-2 text-xs text-slate-500">{e.remarks || '-'}</td>
            {isAdminOnly() && <td className="px-3 py-2"><div className="flex gap-0.5"><Button variant="ghost" size="sm" onClick={() => handleEdit(e)} className="text-blue-600 h-7 w-7 p-0" title="Edit" data-testid={`edit-lh-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="sm" onClick={() => handleDelete(e.id)} className="text-red-600 h-7 w-7 p-0" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button></div></td>}
          </tr>
        ))}</tbody>
      </table>
    );

    if (activeTab === 'financial_support') return (
      <table className="w-full text-sm">
        <thead className={`${headColor} text-white`}><tr>
          <th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-left">Institution</th><th className="px-3 py-2 text-center">Type</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-center">Date</th><th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-left">Remarks</th>{isAdminOnly() && <th className="px-3 py-2"></th>}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">{entries.map((e, i) => (
          <tr key={e.id} className={i % 2 ? 'bg-slate-50' : ''}>
            <td className="px-3 py-2">{e.entrepreneur_id ? <Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link> : '-'}</td>
            <td className="px-3 py-2 text-xs">{e.branch_name || '-'}</td>
            <td className="px-3 py-2 font-medium">{e.institution_name}</td>
            <td className="px-3 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{e.institution_type || '-'}</span></td>
            <td className="px-3 py-2 text-right font-bold">Rs.{(e.support_amount || 0).toLocaleString('en-IN')}</td>
            <td className="px-3 py-2 text-center">{e.support_date ? new Date(e.support_date).toLocaleDateString('en-IN') : '-'}</td>
            <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${e.support_status === 'Disbursed' ? 'bg-emerald-100 text-emerald-800' : e.support_status === 'Sanctioned' ? 'bg-blue-100 text-blue-800' : e.support_status === 'Applied' ? 'bg-amber-100 text-amber-800' : e.support_status === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>{e.support_status || '-'}</span></td>
            <td className="px-3 py-2 text-xs text-slate-500">{e.remarks || '-'}</td>
            {isAdminOnly() && <td className="px-3 py-2"><div className="flex gap-0.5"><Button variant="ghost" size="sm" onClick={() => handleEdit(e)} className="text-blue-600 h-7 w-7 p-0" title="Edit" data-testid={`edit-lh-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="sm" onClick={() => handleDelete(e.id)} className="text-red-600 h-7 w-7 p-0" title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button></div></td>}
          </tr>
        ))}</tbody>
      </table>
    );
    return null;
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="livelihoods-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Livelihoods Cell</h1>
          <p className="text-sm text-slate-600">Skill training, Products, Marketing & Financial support tracking</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="w-56" data-testid="lh-branch-filter"><SelectValue placeholder="All Branches" /></SelectTrigger>
            <SelectContent>
              {canViewAll && <SelectItem value="all">All Branches</SelectItem>}
              {visibleBranches.map(b => (
                <SelectItem key={b.id} value={b.id}>
                  {b.code} - {b.name}{b.id === user?.branch_id ? ' (Home)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => printLivelihoodsList(entries, branches, branchFilter, activeTab, TABS)}
            data-testid="print-livelihoods-button"
            className="border-slate-300"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          {isAdminOnly() && (
            <Button
              type="button"
              variant="outline"
              onClick={runBranchBackfill}
              className="text-orange-700 border-orange-300 hover:bg-orange-50"
              title="Re-align existing livelihood entries to their entrepreneur's actual branch"
              data-testid="fix-branches-btn"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Fix Branches
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={o => { if (!o) { setDialogOpen(false); setEditingEntry(null); setFormData({}); setSelectedEntDetails(null); } else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-livelihood-btn"><Plus className="w-4 h-4 mr-2" />Add {TABS.find(t => t.key === activeTab)?.label} Entry</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingEntry ? 'Edit' : 'Add'} {TABS.find(t => t.key === activeTab)?.label} Entry</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="mt-2">
                {renderForm()}
                <div className="flex justify-end gap-3 mt-6">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingEntry(null); setFormData({}); setSelectedEntDetails(null); }}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="submit-livelihood-btn">{editingEntry ? 'Update Entry' : 'Save Entry'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const colors = { emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800', blue: 'bg-blue-50 border-blue-200 text-blue-800', purple: 'bg-purple-50 border-purple-200 text-purple-800', amber: 'bg-amber-50 border-amber-200 text-amber-800' };
          return (
            <div key={tab.key} className={`rounded-lg border p-3 ${colors[tab.color]} cursor-pointer transition-all ${activeTab === tab.key ? 'ring-2 ring-offset-1 ring-emerald-500' : ''}`} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}>
              <div className="flex items-center gap-2 mb-1"><Icon className="w-4 h-4" /><span className="text-xs font-semibold">{tab.label}</span></div>
              <p className="text-2xl font-bold">{summary[tab.key] || 0}</p>
            </div>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(() => {
              const t = TABS.find(x => x.key === activeTab);
              const Icon = t?.icon || Plus;
              const hint = {
                skill_training: 'Record each skill/job training the beneficiary has attended, their earnings before & after, and current job/business status.',
                product: 'Record products made by the beneficiary (with UDYAM / MSME registration no. if available).',
                marketing: 'Record marketing & promotion activities (exhibitions, melas, online campaigns, retail linkage, etc.)',
                financial_support: 'Record financial support / linkage with banks, MFIs, NBFCs, SHGs, or government schemes.',
              }[activeTab];
              return (
                <>
                  <Icon className="w-4 h-4 text-slate-700" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t?.label}</p>
                    <p className="text-xs text-slate-500">{hint}</p>
                  </div>
                </>
              );
            })()}
          </div>
          <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`add-${activeTab}-inline-btn`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add {TABS.find(t => t.key === activeTab)?.label} Entry
          </Button>
        </div>
        <div className="overflow-x-auto">
          {renderTable()}
        </div>
      </div>
    </div>
  );
};

export default Livelihoods;
